import { handoffAssistantCitationHash } from "../../lib/assistantCitationNavigation";
import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import { transferThreadScrollPosition } from "./threadScrollPosition";
import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import { useComposerDraftStore } from "../../composerDraftStore";
import {
  type ComposerSubmissionAcknowledgement,
  reconcileComposerSubmissionAcknowledgement,
} from "./reconcileRemoteComposerSubmission";
import { stashOlderDestinationThenMove } from "./remoteThreadHandoffCollision";

export function useRemoteThreadHandoff(input: {
  readonly source: ScopedThreadRef;
  readonly destination: ScopedThreadRef | null;
  readonly destinationAcknowledgements: readonly ComposerSubmissionAcknowledgement[];
  readonly ready: boolean;
}): string | null {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const sourceEnvironmentId = input.source.environmentId;
  const threadId = input.source.threadId;
  const destinationEnvironmentId = input.destination?.environmentId ?? null;
  useEffect(() => {
    if (
      !input.ready ||
      !destinationEnvironmentId ||
      destinationEnvironmentId === sourceEnvironmentId
    )
      return;
    const source = { environmentId: sourceEnvironmentId, threadId };
    const destination = { environmentId: destinationEnvironmentId, threadId };
    reconcileComposerSubmissionAcknowledgement(source, input.destinationAcknowledgements);
    reconcileComposerSubmissionAcknowledgement(destination, input.destinationAcknowledgements);
    const store = useComposerDraftStore.getState();
    const moved = store.moveComposerDraft(source, destination);
    if (!moved && !stashOlderDestinationThenMove(source, destination)) {
      setError("The connected thread already has a draft. Your unsent message is preserved here.");
      return;
    }
    transferThreadScrollPosition(scopedThreadKey(source), scopedThreadKey(destination));
    void navigate({
      to: "/$environmentId/$threadId",
      params: { environmentId: destinationEnvironmentId, threadId },
      replace: true,
      hash: (previous) =>
        handoffAssistantCitationHash(
          previous ?? "",
          { environmentId: sourceEnvironmentId, threadId },
          { environmentId: destinationEnvironmentId, threadId },
        ),
      state: (previous) => previous,
      resetScroll: false,
      search: (previous) => previous,
    });
  }, [
    input.ready,
    input.destinationAcknowledgements,
    sourceEnvironmentId,
    destinationEnvironmentId,
    threadId,
    navigate,
  ]);
  return error;
}
